import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Loading from './Loading';

/**
 * DelayedLoading component that waits a specified time before showing the loading indicator.
 * This prevents jarring flash effects for quick loading operations.
 *
 * @param {number} delay - Delay in milliseconds before showing loading (default: 300ms)
 * @param {string} size - Size of the spinner: 'small' or 'normal' (default: 'normal')
 * @param {string} text - Text to display below the spinner (default: from i18n 'common.loading')
 * @param {string} className - Additional CSS classes to apply
 * @param {boolean} fullHeight - Whether to use full available height within layout (default: false)
 *
 * @example
 * // Basic usage with 300ms delay (uses i18n default text)
 * <DelayedLoading fullHeight={true} />
 *
 * @example
 * // Custom delay and text
 * <DelayedLoading delay={500} text="Please wait..." />
 */
function DelayedLoading({ delay = 300, text, ...loadingProps }) {
  const { t } = useTranslation();
  const [showLoading, setShowLoading] = useState(false);
  const defaultText = text || t('common.loading');

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoading(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  if (!showLoading) {
    return null;
  }

  return <Loading text={defaultText} {...loadingProps} />;
}

DelayedLoading.propTypes = {
  delay: PropTypes.number,
  text: PropTypes.string,
};

export default DelayedLoading;
